import type { CSSProperties, RefObject, WheelEvent } from 'react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import classNames from 'classnames'

import './styles.scss'

interface FadeScrollWrapperProps {
  children: JSX.Element
  className?: string
  style?: CSSProperties
}

const FadeScrollWrapper = forwardRef<HTMLDivElement, FadeScrollWrapperProps>(
  ({ children, className, style }: FadeScrollWrapperProps, ref) => {
    const [scrollTop, setScrollTop] = useState(0)
    const [scrollHeight, setScrollHeight] = useState(0)
    const [clientHeight, setClientHeight] = useState(0)

    const onScrollHandler = (event: WheelEvent<HTMLDivElement>) => {
      setScrollTop(event.currentTarget.scrollTop)
      setScrollHeight(event.currentTarget.scrollHeight)
      setClientHeight(event.currentTarget.clientHeight)
    }

    const wrapperRef = useRef<HTMLDivElement | null>(null)

    useImperativeHandle(ref, () => wrapperRef.current!)

    useEffect(() => {
      const resetRefSizes = (ref: RefObject<HTMLDivElement>) => {
        if (!ref.current) return

        setScrollTop(ref.current.scrollTop)
        setScrollHeight(ref.current.scrollHeight)
        setClientHeight(ref.current.clientHeight)
      }

      resetRefSizes(wrapperRef)
    }, [wrapperRef?.current?.clientHeight])

    const getVisibleSides = (): { top: boolean; bottom: boolean } => {
      const isBottom = clientHeight >= scrollHeight - scrollTop
      const isTop = scrollTop <= 0
      const isBetween = scrollTop > 0 && clientHeight < scrollHeight - scrollTop

      return {
        top: (isBottom || isBetween) && !(isTop && isBottom),
        bottom: (isTop || isBetween) && !(isTop && isBottom),
      }
    }

    return (
      <div
        ref={wrapperRef}
        className={classNames('FadeScrollWrapper', className)}
        style={style}
        onScroll={onScrollHandler}
      >
        <div
          className="FadeScrollWrapper-shadow-top"
          style={{
            opacity: getVisibleSides().top ? 1 : 0,
          }}
        />
        {children}
        <div
          className="FadeScrollWrapper-shadow-bottom"
          style={{
            opacity: getVisibleSides().bottom ? 1 : 0,
          }}
        />
      </div>
    )
  }
)

export default FadeScrollWrapper
